import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * Consistent page-header block for list and detail screens. Unifies what
 * was a mix of `<h1>` + flex rows (List screens) vs. identity-card headers
 * (Detail screens). Use `back` to wire the "← Back" link; omit it on list
 * pages.
 *
 * Intentionally plain — no sticky positioning yet. The UX architect audit
 * flagged sticky identity as the next step for detail pages; promoting this
 * component is the obvious landing zone.
 */

export interface PageHeaderProps {
  title: ReactNode;
  /** Small badge / pill row rendered to the right of the title (status, role). */
  badges?: ReactNode;
  /** Tiny secondary text under the title (e.g. "@username", "language: EN"). */
  subtitle?: ReactNode;
  /** Action buttons rendered on the far right of the header. */
  actions?: ReactNode;
  /** Optional "← Back to X" link rendered above the title. */
  back?: { to: string; label: string; testId?: string };
  testId?: string;
}

export default function PageHeader({
  title,
  badges,
  subtitle,
  actions,
  back,
  testId,
}: PageHeaderProps) {
  return (
    <header className="space-y-2" data-testid={testId}>
      {back && (
        <Link
          to={back.to}
          className="inline-block text-sm text-slate-600 hover:text-slate-900"
          data-testid={back.testId}
        >
          ← {back.label}
        </Link>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 truncate">
              {title}
            </h1>
            {badges}
          </div>
          {subtitle && (
            <div className="text-sm text-slate-600 mt-0.5">{subtitle}</div>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 shrink-0">{actions}</div>
        )}
      </div>
    </header>
  );
}
